import { useModals } from 'almanac-ui/Modal'
import { MediaObject, SelectedImage } from 'types'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ImagePickerModal } from 'components/ImagePickerModal/ImagePickerModal'
import { ImagePickerModalContainerProps } from 'components/ImagePickerModal/Types'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { ImagePickerContextState } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { ImagePickerModalV2 } from 'components/ImagePickerModal/ImagePickerModalV2'

interface Props extends Omit<ImagePickerModalContainerProps, 'uploadImages' | 'onConfirm'> {
  handleSubmit: (files: SelectedImage[], context: ImagePickerContextState) => void
  onUploadProgress?: (progress: number) => void
  useV2?: boolean
  skipImageUpload?: boolean
}

export const MediaUploadModal = ({ handleSubmit, onUploadProgress, useV2, skipImageUpload, ...props }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { closeModal } = useModals()

  const { uploadImageFiles } = useImportMediaFiles(
    false,
    () => {
      showToastMessage('All files were uploaded successfully!', 'success')
    },
    onUploadProgress,
  )

  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()

  const addFilesToPreview = async (selectedUploadFiles: SelectedImage[], context: ImagePickerContextState) => {
    handleSubmit(selectedUploadFiles, context)

    closeModal()
  }

  const uploadImages = async (files: File[], context: ImagePickerContextState) => {
    if (skipImageUpload) return context.selectedImages

    let objects: MediaObject[] = []
    if (context.provider === 'uploads') {
      const uploadedImages = await uploadImageFiles(files, uploadsProjectId)
      if (!uploadedImages) return []
      return uploadedImages.map(
        (file) => ({ id: file.id, url: file.source_url, mime_type: file.mime_type } as SelectedImage),
      )
    } else {
      objects = await createMediaObjects(files.map((file) => ({ file, filename: file.name })))
    }

    return objects.map(
      (file) =>
        ({
          id: file.id.toString(),
          url: file.source_url,
          mime_type: file.mime_type,
          original_filename: file.id.toString(),
        } as SelectedImage),
    )
  }

  if (useV2) return <ImagePickerModalV2 uploadImages={uploadImages} onConfirm={addFilesToPreview} {...props} />

  return (
    <ImagePickerModal uploadImages={uploadImages} onConfirm={addFilesToPreview} defaultProvider="uploads" {...props} />
  )
}
